/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { toast } from 'react-toastify';
import Modal from '@material-ui/core/Modal';
import Fade from '@material-ui/core/Fade';
import Backdrop from '@material-ui/core/Backdrop';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Autocomplete from '@material-ui/lab/Autocomplete';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as yup from 'yup';

import { editUser } from '../store/actions/auth';
import { getAdmin, getGroups } from '../store/actions/admin';
import { getPatients } from '../store/actions/patients';

const useStyles = makeStyles((theme) => ({
  title: {
    fontSize: 18,
    margin: '.5em 0 1em',
    color: '#1e3f9e',
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    padding: '2rem',
    paddingTop: '1rem',
    minWidth: 550,
    maxWidth: '700px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    boxShadow: theme.shadows[5],
    outline: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '2rem',
    width: '100%',
    justifyContent: 'space-around',
  },
  button: {
    width: '30%',
    padding: '0.6rem',
    color: 'white',
    backgroundColor: '#00d1b2',
    '&:hover': {
      backgroundColor: '#00bca0',
    },
  },
  form: {
    margin: '1rem',
    width: '100%',
    display: 'flex',
    flexFlow: 'row wrap',
  },
  fieldContainer: {
    width: '40%',
    marginLeft: '5%',
    marginRight: '5%',
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
  },
  field: {
    width: '100%',
  },
  error: {
    color: 'red',
  },
  nameFieldContainer: {
    width: '40%',
    marginLeft: '5%',
    marginRight: '5%',
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
    flexFlow: 'column',
  },
}));

const EditProfileModal = ({ open, setOpen }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const user = useSelector((state) => state.admin.currentUserInfo);
  const users = useSelector((state) => state.admin.usersList);
  const patients = useSelector((state) => state.patients.patientsList);
  const groups = useSelector((state) => state.admin.groupsList);
  const [supervisors, setSupervisors] = useState([]);
  const [chosenSupervisor, setChosenSupervisor] = useState();
  const [chosenPatients, setChosenPatients] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [validationSchema, setValidationSchema] = useState();
  const { id } = user;

  const handleClose = () => {
    setOpen(false);
    setValidationSchema(getValidationSchema());
  };

  useEffect(() => {
    if (!users.length) dispatch(getAdmin());
    if (!patients.length) dispatch(getPatients());
    if (!groups.length) dispatch(getGroups());
  }, []);

  useEffect(() => {
    setValidationSchema(getValidationSchema());
    if (user.supervisor) setChosenSupervisor(user.supervisor);
    if (user.patients) setChosenPatients(user.patients);
    if (user.groups) setSelectedGroups(user.groups);
  }, [user]);

  useEffect(() => {
    if (users.length) {
      setSupervisors(users.filter((checkUser) => checkUser.role && checkUser.role.name === 'Supervisor'));
    }
  }, [users]);

  const submitHandler = (values) => {
    const body = {
      ...values,
      email: values.email.toLowerCase(),
      supervisor: chosenSupervisor ? chosenSupervisor.id : null,
    };
    body.patients = chosenPatients.map((patient) => patient.id);
    body.groups = selectedGroups.map((group) => group.id);
    dispatch(editUser(body, id));
    setOpen(false);
    toast.success('User information saved.', {
      position: 'bottom-left',
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      theme: 'colored',
    });
  };

  const getValidationSchema = () => {
    return yup.object({
      first_name: yup.string('Enter First Name').required('*Required'),
      last_name: yup.string('Enter Last Name').required('*Required'),
      email: yup.string('Enter Email').email('Enter a valid email').required('*Required'),
      phone_number:
        user && user.role && user.role.name === 'Supervisor'
          ? yup
              .string()
              .matches(/^\+[1-9]\d{10,14}$/, 'Phone number must be in E164 format (ie +12223334444)')
              .required('*Required')
          : yup.string().matches(/^\+[1-9]\d{10,14}$/, 'Phone number must be in E164 format (ie +12223334444)'),
    });
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modal}
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
    >
      <Fade in={open}>
        <div className={classes.paper}>
          <h2 className={classes.title}>Edit User Information</h2>
          <Formik
            initialValues={{
              first_name: user.first_name,
              last_name: user.last_name,
              email: user.email,
              phone_number: user.phone_number || '',
            }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              setTimeout(() => {
                submitHandler(values);
                setSubmitting(false);
              }, 400);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              /* and other goodies */
            }) => {
              return (
                <form className={classes.form} onSubmit={handleSubmit}>
                  <div className={classes.nameFieldContainer}>
                    <TextField
                      className={classes.field}
                      placeholder="Enter First Name"
                      variant="outlined"
                      name="first_name"
                      defaultValue={values.first_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <p className={classes.error}>{errors.first_name && touched.first_name && errors.first_name}</p>
                  </div>
                  <div className={classes.nameFieldContainer}>
                    <TextField
                      className={classes.field}
                      placeholder="Enter Last Name"
                      variant="outlined"
                      name="last_name"
                      defaultValue={values.last_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <p className={classes.error}>{errors.last_name && touched.last_name && errors.last_name}</p>
                  </div>
                  <div className={classes.fieldContainer}>
                    <TextField
                      className={classes.field}
                      placeholder="Enter Email"
                      variant="outlined"
                      name="email"
                      defaultValue={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <p className={classes.error}>{errors.email && touched.email && errors.email}</p>
                  </div>
                  <div className={classes.fieldContainer}>
                    <TextField
                      className={classes.field}
                      placeholder="Enter Phone Number"
                      variant="outlined"
                      name="phone_number"
                      defaultValue={values.phone_number}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <p className={classes.error}>
                      {errors.phone_number && touched.phone_number && errors.phone_number}
                    </p>
                  </div>
                  {user && user.role && user.role.name === 'HCP' && (
                    <div className={classes.fieldContainer}>
                      {supervisors && (
                        <Autocomplete
                          options={supervisors}
                          className={classes.field}
                          getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                          id="auto-complete"
                          getOptionSelected={(option, value) => option.id === value.id}
                          autoComplete
                          defaultValue={user.supervisor}
                          onChange={(event, newValue) => setChosenSupervisor(newValue)}
                          renderInput={(params) => <TextField {...params} label="Supervisor" margin="normal" />}
                        />
                      )}
                    </div>
                  )}
                  <div className={classes.fieldContainer}>
                    {patients && (
                      <Autocomplete
                        options={patients}
                        multiple
                        className={classes.field}
                        getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                        id="auto-complete"
                        getOptionSelected={(option, value) => option.id === value.id}
                        autoComplete
                        defaultValue={user.patients}
                        onChange={(event, newValue) => setChosenPatients(newValue)}
                        renderInput={(params) => <TextField {...params} label="Patients" margin="normal" />}
                      />
                    )}
                  </div>
                  <div className={classes.nameFieldContainer}>
                    {groups && (
                      <Autocomplete
                        multiple
                        options={groups}
                        className={classes.field}
                        getOptionLabel={(option) => option.name}
                        id="auto-complete"
                        getOptionSelected={(option, value) => option.id === value.id}
                        autoComplete
                        defaultValue={selectedGroups}
                        onChange={(event, newValue) => setSelectedGroups(newValue)}
                        renderInput={(params) => <TextField {...params} label="Group" margin="normal" />}
                      />
                    )}
                  </div>
                  <div className={classes.buttonRow}>
                    <Button
                      className={classes.button}
                      size="small"
                      variant="contained"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      Save Changes
                    </Button>
                    <Button className={classes.button} size="small" variant="contained" onClick={handleClose}>
                      Cancel
                    </Button>
                  </div>
                </form>
              );
            }}
          </Formik>
        </div>
      </Fade>
    </Modal>
  );
};

EditProfileModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
};

export default EditProfileModal;
